"use client"

import { useEffect, useState } from "react"
import { toast } from "sonner"
import { useTranslations } from "next-intl"
import { IconCalendar } from "@tabler/icons-react"

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { saveCallDisposition } from "@/lib/phone-calls-api"
import { getCallOutcomes, type CallOutcome } from "@/lib/call-outcomes-api"
import { localInputToIso } from "@/lib/local-datetime"
import { useAuthStore } from "@/stores/auth-store"

const DISPOSITION_VALUES = [
  "answered",
  "voicemail",
  "callback_needed",
  "not_interested",
  "wrong_number",
  "no_answer",
] as const

// Phase 80 (CCR-04) — module-level cache: the catalog is fetched once per
// browser session (first dialog open), not re-fetched on every subsequent
// disposition save (80-UI-SPEC §8.2 "cached per session").
let cachedActiveOutcomes: CallOutcome[] | null = null

interface CallContext {
  uniqueid?: string
  direction?: string
  number?: string
  extension?: string
  leadId?: string
  leadName?: string
  midCallNotes?: string
}

interface PostCallDispositionModalProps {
  open: boolean
  onClose: () => void
  callContext: CallContext
}

export function LegacyPostCallDispositionModal({
  open,
  onClose,
  callContext,
}: PostCallDispositionModalProps) {
  const t = useTranslations("telephony")
  // Flag read FIRST — every catalog branch below short-circuits off this,
  // so a flag-off render never fetches and never diverges from the
  // hardcoded-list path (80-UI-SPEC §8.2, C8).
  const ccrOn = useAuthStore((s) => s.user?.feature_flags?.call_center_reporting_enabled ?? false)
  const [disposition, setDisposition] = useState("")
  const [selectedOutcome, setSelectedOutcome] = useState<CallOutcome | null>(null)
  const [notes, setNotes] = useState(callContext.midCallNotes || "")
  const [followUpAt, setFollowUpAt] = useState("")
  const [followUpTitle, setFollowUpTitle] = useState("")
  const [isSaving, setIsSaving] = useState(false)
  const [outcomes, setOutcomes] = useState<CallOutcome[]>(cachedActiveOutcomes ?? [])
  const [isLoadingOutcomes, setIsLoadingOutcomes] = useState(false)

  const dispositionLabels: Record<(typeof DISPOSITION_VALUES)[number], string> = {
    answered: t("postCallDisposition.answered"),
    voicemail: t("postCallDisposition.voicemail"),
    callback_needed: t("postCallDisposition.callbackNeeded"),
    not_interested: t("postCallDisposition.notInterested"),
    wrong_number: t("postCallDisposition.wrongNumber"),
    no_answer: t("postCallDisposition.noAnswer"),
  }

  useEffect(() => {
    if (!open || !ccrOn) return
    const timer = setTimeout(() => {
    if (cachedActiveOutcomes) {
      setOutcomes(cachedActiveOutcomes)
      return
    }
    setIsLoadingOutcomes(true)
    getCallOutcomes()
      .then((data) => {
        const active = data.filter((o) => o.is_active).sort((a, b) => a.order - b.order)
        cachedActiveOutcomes = active
        setOutcomes(active)
      })
      .catch((error) => {
        // C8: a fetch failure must never block saving mid-call — leave
        // `outcomes` empty so the render falls back to DISPOSITION_VALUES.
        console.error(error)
      })
      .finally(() => setIsLoadingOutcomes(false))
    }, 0)
    return () => clearTimeout(timer)
  }, [open, ccrOn])

  // The catalog path is only "live" once it actually has rows to offer;
  // a fetch error (or a genuinely empty catalog) falls through to the
  // hardcoded list rather than rendering an empty Select.
  const useCatalog = ccrOn && (outcomes.length > 0 || isLoadingOutcomes)

  const handleOutcomeSelect = (value: string) => {
    if (useCatalog) {
      const outcome = outcomes.find((o) => o.id === value) ?? null
      setSelectedOutcome(outcome)
      // Legacy string continuity: `disposition` carries the outcome's code.
      setDisposition(outcome?.code ?? "")
    } else {
      setDisposition(value)
    }
  }

  const handleSave = async () => {
    if (!disposition) {
      toast.error(t("postCallDisposition.selectDispositionError"))
      return
    }

    setIsSaving(true)
    try {
      const fromNumber = callContext.direction === "inbound" ? callContext.number : undefined
      const toNumber = callContext.direction === "outbound" ? callContext.number : undefined

      await saveCallDisposition({
        pbx_uniqueid: callContext.uniqueid,
        extension: callContext.extension,
        direction: callContext.direction,
        from_number: fromNumber,
        to_number: toNumber,
        lead_id: callContext.leadId,
        disposition,
        notes: notes || undefined,
        follow_up_at: localInputToIso(followUpAt) ?? undefined,
        follow_up_title: followUpTitle || undefined,
        outcome_id: useCatalog ? selectedOutcome?.id : undefined,
      })
      toast.success(t("postCallDisposition.savedToast"))
      onClose()
    } catch (error) {
      console.error(error)
      toast.error(t("postCallDisposition.saveFailedToast"))
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("postCallDisposition.title")}</DialogTitle>
          <DialogDescription>
            {callContext.leadName
              ? t("postCallDisposition.callWith", { name: callContext.leadName })
              : callContext.number
                ? t("postCallDisposition.callWith", { name: callContext.number })
                : t("postCallDisposition.recordOutcome")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Disposition select */}
          <div className="space-y-2">
            <Label>{t("postCallDisposition.outcome")}</Label>
            <Select
              value={useCatalog ? (selectedOutcome?.id ?? "") : disposition}
              onValueChange={handleOutcomeSelect}
              disabled={ccrOn ? isLoadingOutcomes && outcomes.length === 0 : undefined}
            >
              <SelectTrigger>
                <SelectValue placeholder={t("postCallDisposition.selectPlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                {useCatalog
                  ? outcomes.map((outcome) => (
                      <SelectItem key={outcome.id} value={outcome.id}>
                        <span className="flex items-center gap-2">
                          <span
                            className="size-2 rounded-full ring-1 ring-border"
                            style={{ backgroundColor: outcome.color }}
                          />
                          {outcome.name}
                        </span>
                      </SelectItem>
                    ))
                  : DISPOSITION_VALUES.map((value) => (
                      <SelectItem key={value} value={value}>
                        {dispositionLabels[value]}
                      </SelectItem>
                    ))}
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label>{t("postCallDisposition.notes")}</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t("postCallDisposition.notesPlaceholder")}
              rows={3}
            />
          </div>

          {/* Follow-up */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5">
              <IconCalendar className="h-3.5 w-3.5" />
              {t("postCallDisposition.followUp")}
            </Label>
            <div className="grid grid-cols-2 gap-2">
              <Input
                type="datetime-local"
                value={followUpAt}
                onChange={(e) => setFollowUpAt(e.target.value)}
              />
              <Input
                placeholder={t("postCallDisposition.reminderTitlePlaceholder")}
                value={followUpTitle}
                onChange={(e) => setFollowUpTitle(e.target.value)}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={onClose} disabled={isSaving}>
              {t("postCallDisposition.skip")}
            </Button>
            <Button onClick={handleSave} disabled={isSaving || !disposition}>
              {isSaving ? t("postCallDisposition.saving") : t("postCallDisposition.saveAndClose")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
